import { useEffect, useState } from 'react'
import _ from 'lodash'
import qs from 'query-string'
import {useUpdateEffect} from 'react-use'
import { useLocation, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { CATEGORY_OF_PRODUCT_PROPS_ID, CATEGORIES_OF_PRODUCT_PROPS_ID } from "src/common/razzle-platform-categoryofproduct/ssr"
import { fetchCategoryOfProduct, fetchCategoriesOfProduct } from "src/common/razzle-platform-categoryofproduct/effects"
import { IParamsOption, IRequestFetcherOptions, Pagination } from 'src/common/razzle-platform-components/types'


type UseCategoryOfProduct<T> = {
    categoryOfProduct: T,
    isFirstRequestInPregress: boolean
}

export function useCategoryOfProduct<T>(options: Pick<IParamsOption, 'params'> = {}): UseCategoryOfProduct<T> {
    const params = useParams()
    const dispatch = useDispatch()
    const locale: string = useSelector((state: any) => state.translationModel.locale)
    // get the initial state of categoryOfProductProps by it sheared id
    const initialProps = useSelector((state: any) => state.ssrModel[CATEGORY_OF_PRODUCT_PROPS_ID]) || {}
    const [isFirstRequestInPregress, setFirstRequestInPregress] = useState(false)
    // we use this state to save our category of product
    const [state, setState] = useState({ categoryOfProduct: initialProps.categoryOfProduct })

    useEffect(() => {
        const id = _.get(params, 'id')

        if (initialProps.categoryOfProduct) {
            // case when we make reload or refresh on the browser we get the saved category of product form the server (window.__PRELOADED_STATE__)

            // we remove all initialProps of categoryOfProductProps except queryOptions because we need it to use later in the client part
            dispatch.ssrModel.cleanInitialProps(CATEGORY_OF_PRODUCT_PROPS_ID)
        } else {
            // case when we are on browser (pass from page to another) we don't need to get category of product form the server side

            // we make request if we don't have category of product or the category of product it's diffirente to saved category of product
            if (!state.categoryOfProduct || state.categoryOfProduct.id !== id) {

                // in category of product we need params for make forced request exmaple: parms: { _embed: [user, ...etc]}
                const queryOptions: Pick<IRequestFetcherOptions, 'params'> = {
                    params: options.params,
                }

                // set the Accept-Language to the headers of the request for get the translated data
                const headers = { "Accept-Language": locale }

                const fetchData = async () => {
                    setFirstRequestInPregress(true)
                    // get the category of product by execute the effect fetchCategoryOfProduct and pass it queryOptions
                    const { categoryOfProduct } = await fetchCategoryOfProduct(id, queryOptions, headers)
                    // save the category of product in our state
                    setState({ categoryOfProduct })
                    setFirstRequestInPregress(false)
                }
                fetchData()
            }
        }

	}, [])

    return { categoryOfProduct: state.categoryOfProduct, isFirstRequestInPregress }

}


type UseCategoriesOfProduct<T> = {
    categoriesOfProduct: Array<T>,
    pagination: Pagination,
    isFirstRequestInPregress: boolean,
    isUpdateProgress: boolean
}

export function useCategoriesOfProduct<T>(options: IParamsOption={params: null, useUrlParams: true, usePagination: true }): UseCategoriesOfProduct<T>  {
    const location = useLocation()
    const dispatch = useDispatch()
    const locale: string = useSelector((state: any) => state.translationModel.locale)
    // get the initial state of categoriesOfProductProps by it sheared id
    const initialProps = useSelector((state: any) => state.ssrModel[CATEGORIES_OF_PRODUCT_PROPS_ID]) || {}
    // state use when we update the categories of product (change page, filters, ...ect)
    const [isUpdateProgress, setUpdateInProgress] = useState(false)
    // use this state in the first render for use loader page
    const [isFirstRequestInPregress, setFirstRequestInPregress] = useState(false)
    // we use this state to save our categories of product and thier pagintion
    const [state, setState] = useState({ categoriesOfProduct: initialProps.categoriesOfProduct, pagination: initialProps.pagination })

    const queryOptions: IRequestFetcherOptions = {
        // prams given from url
        searchParams: qs.parse(location.search),
        // forced params by user not it's shown in url
        params: options.params,
        // to allow use pagination
        usePagination: options.usePagination,
        // to allow use search parms from url
        useUrlParams: options.useUrlParams
    }

    // set the Accept-Language to the headers of the request for get the translated data
    const headers = { "Accept-Language": locale }

    useEffect(() => {

        if (initialProps.categoriesOfProduct) {
            // case when we make reload or refresh on the browser we get the saved categories of product form the server (window.__PRELOADED_STATE__)

            // we remove all initialProps of categoriesOfProductProps except queryOptions because we need it to use later in the client part
            dispatch.ssrModel.cleanInitialProps(CATEGORIES_OF_PRODUCT_PROPS_ID)
        } else {
            // case when we are on browser (pass from page to another) we don't need to get categories of product form the server side
            const fetchData = async () => {
                setFirstRequestInPregress(true)
                // get the categories of product by execute the effect fetchCategoriesOfProduct and pass it queryOptions
                const {categoriesOfProduct, pagination} = await fetchCategoriesOfProduct(queryOptions, headers)
                // save the categories of product in our state
                setState({ categoriesOfProduct, pagination })
                setFirstRequestInPregress(false)
            }
            fetchData()
        }
    }, [])

    useUpdateEffect(() => {
        // we use this effects when we update the url (change the page, filters, ...etc)

        // check if the update by url is allowed
        if (options.useUrlParams) {

            const fetchData = async () => {
                setUpdateInProgress(true)
                const {categoriesOfProduct, pagination} = await fetchCategoriesOfProduct(queryOptions, headers)
                setState({ categoriesOfProduct, pagination })
                setUpdateInProgress(false)
            }
            fetchData()
        }

    }, [location.search])

    return {
        categoriesOfProduct: state.categoriesOfProduct,
        pagination: state.pagination,
        isFirstRequestInPregress,
        isUpdateProgress
    }
}
